import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  CircularProgress, 
  Alert,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useLocation, useNavigate } from 'react-router-dom';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { mainApi } from '../../../config/apiClient';
import { BRAND } from '../../../config/brand';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// Veri Tipleri
interface QRMenuData {
  id: string;
  name: string;
  branchId?: string | null;
  branchName?: string;
}

interface TableData {
  id: number;
  tableNumber: number;
  tableName?: string;
  createdAt: string;
}

// Styled Components
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  // Mobil için responsive ayarlar
  '@media (max-width: 1499px)': {
    padding: theme.spacing(2),
    height: 'auto',
    overflowY: 'visible',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const TableCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.warning.main,
  },
  '&.selected': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  }
}));

const TableIcon = styled(TableRestaurantIcon)(({ theme }) => ({
  fontSize: '2rem',
  color: theme.palette.warning.main,
  marginBottom: theme.spacing(0.5),
}));

// TablesPage arayüzü
interface TablesPageProps {
  isManager?: boolean;
  onNavigateBack?: () => void;
  branchId?: string;
}

const TablesPage: React.FC<TablesPageProps> = ({ isManager = false, onNavigateBack, branchId }) => {
  const { t, i18n } = useTranslation('salesSystem');
  const dateLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  
  const [qrMenus, setQRMenus] = useState<QRMenuData[]>([]);
  const [tablesData, setTablesData] = useState<Record<string, TableData[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [editingTable, setEditingTable] = useState<{menuId: string, table: TableData} | null>(null);
  const [newTableName, setNewTableName] = useState('');
  const [customDomain, setCustomDomain] = useState<string>('');
  const [downloadingBulkQR, setDownloadingBulkQR] = useState<string | null>(null);
  const [deletingTable, setDeletingTable] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<{menuId: string, table: TableData} | null>(null);
  const [initialMenuId, setInitialMenuId] = useState<string | null>(null);
  
  // Progress Modal States
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressData, setProgressData] = useState({
    menuName: '',
    totalTables: 0,
    completedTables: 0,
    currentTable: '',
    status: 'preparing' as 'preparing' | 'generating' | 'zipping' | 'completed' | 'error',
    errorMessage: '',
    tableProgress: [] as Array<{
      tableNumber: number;
      tableName?: string;
      status: 'waiting' | 'processing' | 'completed' | 'error';
      error?: string;
    }>
  });

  useEffect(() => {
    const menuIdFromQuery = new URLSearchParams(location.search).get('menuId');
    let menuIdFromSession: string | null = null;

    try {
      menuIdFromSession = sessionStorage.getItem('qrmenu_selected_table_menu_id');
      sessionStorage.removeItem('qrmenu_selected_table_menu_id');
    } catch {
      // no-op
    }

    const resolvedMenuId = menuIdFromQuery || menuIdFromSession;
    if (resolvedMenuId) {
      setInitialMenuId(resolvedMenuId);
    }
  }, [location.search]);

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (uid) {
        try {
          const userData = await firebaseDataService.getUserData(uid);
          if (userData?.companyId) {
            setCompanyId(userData.companyId);
          } else {
            setError(t('qrMenuTablesPage.errors.userCompanyNotFound'));
            setLoading(false);
          }
        } catch (error) {
          console.error("Şirket ID'si alınırken hata:", error);
          setError(t('qrMenuTablesPage.errors.companyIdLoadFailed'));
          setLoading(false);
        }
      } else {
        setError(t('qrMenuTablesPage.errors.userNotLoggedIn'));
        setLoading(false);
      }
    };
    fetchCompanyId();
  }, []);

  // QR Menüleri yükle
  useEffect(() => {
    if (!companyId) return;

    const fetchQRMenus = async () => {
      try {
        console.log(`🔄 QR menüleri yükleniyor (Şirket: ${companyId})`);
        
        const response = await mainApi.get(`/api/QRMenu/menu-definitions`);
        const result = response.data;
        if (result.success && result.data) {
          console.log(`✅ ${result.data.length} QR menü yüklendi`);
          
          let menuList: QRMenuData[] = result.data.map((menu: any) => ({
            id: menu.id,
            name: menu.name || t('qrMenuTablesPage.table.unnamedMenu'),
            branchId: menu.branchId || null,
            branchName: menu.branchName || (menu.branchId ? t('qrMenuTablesPage.table.unnamedBranch') : undefined)
          }));
          
          if (isManager && branchId) {
            console.log(`🔍 Manager filtresi uygulanıyor - Şube ID: ${branchId}`);
            menuList = menuList.filter(menu => menu.branchId === branchId);
            console.log(`✅ ${menuList.length} menü manager şubesi için filtrelendi`);
          }
          
          setQRMenus(menuList.sort((a, b) => a.name.localeCompare(b.name)));
        } else {
          setQRMenus([]);
        }
      } catch (err) {
        console.error('❌ QR menü yükleme hatası:', err);
        setError(t('qrMenuTablesPage.errors.menuLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchQRMenus();
  }, [companyId, branchId, isManager]);

  // Custom domain'i SQL'den yükle
  useEffect(() => {
    if (!companyId) return;

    const loadCustomDomainFromSQL = async () => {
      try {
        console.log(`🔄 SQL'den custom domain yükleniyor (Şirket: ${companyId})`);
        
        const response = await mainApi.get(`/api/QRMenu/custom-domain`);
        const result = response.data;
        if (result.success && result.data) {
          console.log(`✅ Custom domain yüklendi: ${result.data.customDomain}`);
          setCustomDomain(result.data.customDomain || '');
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.log('ℹ️ Şirket henüz SQL\'de kayıtlı değil, boş custom domain');
          setCustomDomain('');
        } else {
          console.error('❌ Custom domain yükleme hatası:', error);
        }
      }
    };

    loadCustomDomainFromSQL();
  }, [companyId]);

  // Masaları yükle
  const loadTablesForMenu = async (menuId: string) => {
    if (!companyId) return;

    try {
      console.log(`🔄 Masalar yükleniyor (Menü: ${menuId})`);
      
      const response = await mainApi.get(`/api/Table/company/${companyId}/qrmenu/${menuId}/tables`);
      const result = response.data;
      if (result.success && result.data) {
        console.log(`✅ ${result.data.length} masa yüklendi`);
        setTablesData(prev => ({
          ...prev,
          [menuId]: result.data
        }));
      } else {
        setTablesData(prev => ({
          ...prev,
          [menuId]: []
        }));
      }
    } catch (err) {
      console.error('❌ Masa yükleme hatası:', err);
      setTablesData(prev => ({
        ...prev,
        [menuId]: []
      }));
    }
  };

  useEffect(() => {
    if (!initialMenuId || loading || qrMenus.length === 0) return;

    const exists = qrMenus.some((menu) => menu.id === initialMenuId);
    if (!exists) return;

    setExpandedMenus(new Set([initialMenuId]));
    if (!tablesData[initialMenuId]) {
      loadTablesForMenu(initialMenuId);
    }
  }, [initialMenuId, loading, qrMenus, tablesData]);

  // Accordion açma/kapama
  const handleAccordionChange = (menuId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      // Tekli mod: sadece seçilen menü açık kalsın
      setExpandedMenus(new Set([menuId]));
      // Masaları yükle
      if (!tablesData[menuId]) {
        loadTablesForMenu(menuId);
      }
    } else {
      setExpandedMenus(new Set());
    }
  };

  // Masa adını düzenle
  const handleEditTable = (menuId: string, table: TableData) => {
    setEditingTable({menuId, table});
    setNewTableName(
      table.tableName
        || t('qrMenuTablesPage.table.tableWithNumber', { number: table.tableNumber })
    );
  };

  // Masa adını kaydet
  const handleSaveTableName = async () => {
    if (!editingTable || !companyId) return;

    try {
      await mainApi.post(
        `/api/Table/company/${companyId}/qrmenu/${editingTable.menuId}/table/${editingTable.table.id}/name`,
        { tableName: newTableName.trim() }
      );

      setTablesData(prev => ({
        ...prev,
        [editingTable.menuId]: prev[editingTable.menuId].map(table =>
          table.id === editingTable.table.id
            ? { ...table, tableName: newTableName.trim() }
            : table
        )
      }));
      
      setEditingTable(null);
      setNewTableName('');
    } catch (err) {
      console.error('❌ Masa adı güncelleme hatası:', err);
      setError(t('qrMenuTablesPage.errors.updateTableNameFailed'));
    }
  };

  // Masa silme onay modalını aç
  const handleDeleteTableClick = (menuId: string, table: TableData) => {
    setTableToDelete({menuId, table});
    setDeleteConfirmOpen(true);
  };

  // Masa silme işlemi
  const handleDeleteTable = async () => {
    if (!tableToDelete || !companyId) return;

    const { menuId, table } = tableToDelete;
    setDeletingTable(table.id);

      try {
      console.log(`🗑️ Masa siliniyor: ${table.tableName || `Masa ${table.tableNumber}`} (ID: ${table.id})`);
      
      const response = await mainApi.post(
        `/api/Table/company/${companyId}/qrmenu/${menuId}/table/${table.id}`
      );
      
      console.log(`✅ Masa başarıyla silindi:`, response.data);
      
      setTablesData(prev => ({
        ...prev,
        [menuId]: prev[menuId].filter(t => t.id !== table.id)
      }));
      
      setDeleteConfirmOpen(false);
      setTableToDelete(null);
    } catch (err: any) {
      if (err.response?.data) {
        console.error('❌ Masa silme hatası:', err.response.data);
        setError(err.response.data.message || t('qrMenuTablesPage.errors.deleteTableFailed'));
      } else {
        console.error('❌ Masa silme hatası:', err);
        setError(t('qrMenuTablesPage.errors.deleteTableFailed'));
      }
    } finally {
      setDeletingTable(null);
    }
  };

  // Masa QR kodu indirme fonksiyonu
  const handleDownloadTableQR = async (menuId: string, table: TableData) => {
    // Custom domain varsa onu kullan, yoksa varsayılan adresi kullan
    const baseUrl = customDomain 
      ? `https://${customDomain}/menu/${menuId}_${table.tableNumber}`
      : BRAND.urls.qrMenu(`${menuId}_${table.tableNumber}`);
    
    const qrContent = baseUrl;
    
    // 4K kalitede QR kod için ultra yüksek çözünürlük ayarları
    const qrOptions: QRCode.QRCodeToDataURLOptions = {
      errorCorrectionLevel: 'H', // En yüksek hata düzeltme seviyesi
      type: 'image/png', // PNG daha kaliteli
      margin: 4, // Daha geniş kenar boşluğu
      width: 3840, // 4K genişlik (3840x3840)
      color: {
        dark: '#000000', // Saf siyah QR kod
        light: '#FFFFFF', // Saf beyaz arka plan
      },
    };

    try {
      console.log(`🔄 Masa QR kodu oluşturuluyor: ${qrContent}`);
      
      // Önce QR kodu oluştur
      const qrDataUrl = await QRCode.toDataURL(qrContent, qrOptions);
      
      // Canvas oluştur ve QR kodu yükle
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Canvas boyutunu ayarla
        canvas.width = img.width;
        canvas.height = img.height;
        
        // QR kodunu canvas'a çiz
        ctx!.drawImage(img, 0, 0);
        
        // Ortada beyaz kare oluştur (masa numarası için)
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const squareSize = canvas.width * 0.15; // QR kodun %15'i kadar kare
        
        // Beyaz kare çiz
        ctx!.fillStyle = '#FFFFFF';
        ctx!.fillRect(
          centerX - squareSize / 2, 
          centerY - squareSize / 2, 
          squareSize, 
          squareSize
        );
        
        // Kare etrafına siyah border ekle
        ctx!.strokeStyle = '#000000';
        ctx!.lineWidth = 8;
        ctx!.strokeRect(
          centerX - squareSize / 2, 
          centerY - squareSize / 2, 
          squareSize, 
          squareSize
        );
        
        // Masa numarasını yaz
        const fontSize = squareSize * 0.4; // Karenin %40'ı kadar font
        ctx!.font = `bold ${fontSize}px Arial, sans-serif`;
        ctx!.fillStyle = '#000000';
        ctx!.textAlign = 'center';
        ctx!.textBaseline = 'middle';
        
        // Masa numarasını ortala ve yaz
        const tableText = table.tableNumber.toString();
        ctx!.fillText(tableText, centerX, centerY);
        
        // Canvas'ı blob'a çevir ve indir
        canvas.toBlob((blob) => {
          if (blob) {
            const tableName = table.tableName || `Masa_${table.tableNumber}`;
            const fileName = `${tableName.replace(/[^a-zA-Z0-9-_]/g, '_')}-QR-4K.png`;
            
            console.log(`✅ Masa QR kodu başarıyla oluşturuldu: ${fileName}`);
            saveAs(blob, fileName);
          }
        }, 'image/png');
      };
      
      img.onerror = () => {
        console.error('QR kod resmi yüklenemedi');
        setError(t('qrMenuTablesPage.errors.qrImageLoadFailed'));
      };
      
      img.src = qrDataUrl;
      
    } catch (err) {
      console.error('Masa QR kodu oluşturulurken hata:', err);
      setError(t('qrMenuTablesPage.errors.qrCreateFailed'));
    }
  };

  // Toplu QR kod indirme fonksiyonu - Progress Modal ile
  const handleDownloadBulkQR = async (menuId: string, menuName: string, tables: TableData[]) => {
    if (tables.length === 0) {
      setError(t('qrMenuTablesPage.errors.noTablesInMenu'));
      return;
    }

    // Progress modal'ı başlat
    setProgressData({
      menuName,
      totalTables: tables.length,
      completedTables: 0,
      currentTable: '',
      status: 'preparing',
      errorMessage: '',
      tableProgress: tables.map(table => ({
        tableNumber: table.tableNumber,
        tableName: table.tableName,
        status: 'waiting'
      }))
    });
    setProgressModalOpen(true);
    setDownloadingBulkQR(menuId);

    try {
      console.log(`🔄 ${tables.length} masa için toplu QR kod oluşturuluyor...`);
      
      // ZIP hazırlığı
      setProgressData(prev => ({ ...prev, status: 'generating', currentTable: t('qrMenuTablesPage.bulkDownload.zipPreparing') }));
      const zip = new JSZip();
      const qrFolder = zip.folder(`${menuName.replace(/[^a-zA-Z0-9-_]/g, '_')}_QR_Kodlari`);
      
      // Masaları sırayla işle (paralel yerine sıralı - progress göstermek için)
      const qrResults: Array<{ fileName: string; blob: Blob }> = [];
      
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        
        // Progress güncelle
        setProgressData(prev => ({
          ...prev,
          currentTable: t('qrMenuTablesPage.bulkDownload.processingTable', { number: table.tableNumber }),
          tableProgress: prev.tableProgress.map((tp, idx) => 
            idx === i ? { ...tp, status: 'processing' } : tp
          )
        }));

        try {
          // URL oluştur
          const baseUrl = customDomain 
            ? `https://${customDomain}/menu/${menuId}_${table.tableNumber}`
            : BRAND.urls.qrMenu(`${menuId}_${table.tableNumber}`);
          
          // QR kod ayarları
          const qrOptions: QRCode.QRCodeToDataURLOptions = {
            errorCorrectionLevel: 'H',
            type: 'image/png',
            margin: 4,
            width: 3840, // 4K kalite
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          };

          // QR kodu oluştur
          const qrDataUrl = await QRCode.toDataURL(baseUrl, qrOptions);
          
          // Canvas ile masa numarasını ekle
          const result = await new Promise<{ fileName: string; blob: Blob }>((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
              canvas.width = img.width;
              canvas.height = img.height;
              
              // QR kodunu çiz
              ctx!.drawImage(img, 0, 0);
              
              // Ortada beyaz kare
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;
              const squareSize = canvas.width * 0.15;
              
              // Beyaz kare
              ctx!.fillStyle = '#FFFFFF';
              ctx!.fillRect(
                centerX - squareSize / 2, 
                centerY - squareSize / 2, 
                squareSize, 
                squareSize
              );
              
              // Siyah border
              ctx!.strokeStyle = '#000000';
              ctx!.lineWidth = 8;
              ctx!.strokeRect(
                centerX - squareSize / 2, 
                centerY - squareSize / 2, 
                squareSize, 
                squareSize
              );
              
              // Masa numarası
              const fontSize = squareSize * 0.4;
              ctx!.font = `bold ${fontSize}px Arial, sans-serif`;
              ctx!.fillStyle = '#000000';
              ctx!.textAlign = 'center';
              ctx!.textBaseline = 'middle';
              ctx!.fillText(table.tableNumber.toString(), centerX, centerY);
              
              // Blob'a çevir
              canvas.toBlob((blob) => {
                if (blob) {
                  const tableName = table.tableName || `Masa_${table.tableNumber}`;
                  const fileName = `${tableName.replace(/[^a-zA-Z0-9-_]/g, '_')}-QR-4K.png`;
                  resolve({ fileName, blob });
                } else {
                  reject(new Error(`Masa ${table.tableNumber} için QR kod oluşturulamadı`));
                }
              }, 'image/png');
            };
            
            img.onerror = () => reject(new Error(`Masa ${table.tableNumber} için resim yüklenemedi`));
            img.src = qrDataUrl;
          });

          qrResults.push(result);
          
          // Progress güncelle - başarılı
          setProgressData(prev => ({
            ...prev,
            completedTables: i + 1,
            tableProgress: prev.tableProgress.map((tp, idx) => 
              idx === i ? { ...tp, status: 'completed' } : tp
            )
          }));

        } catch (error) {
          console.error(`Masa ${table.tableNumber} QR kodu oluşturulurken hata:`, error);
          
          // Progress güncelle - hata
          setProgressData(prev => ({
            ...prev,
            tableProgress: prev.tableProgress.map((tp, idx) => 
              idx === i ? { ...tp, status: 'error', error: error instanceof Error ? error.message : t('qrMenuTablesPage.bulkDownload.unknownError') } : tp
            )
          }));
        }

        // Kısa bir bekleme (UI güncellemesi için)
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // ZIP oluşturma
      setProgressData(prev => ({ ...prev, status: 'zipping', currentTable: t('qrMenuTablesPage.bulkDownload.zipBuilding') }));
      
      // ZIP dosyasına ekle
      qrResults.forEach(({ fileName, blob }) => {
        qrFolder!.file(fileName, blob);
      });
      
      // ZIP dosyasını oluştur ve indir
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `${menuName.replace(/[^a-zA-Z0-9-_]/g, '_')}_Tum_Masalar_QR_4K.zip`;
      
      console.log(`✅ Toplu QR kodları başarıyla oluşturuldu: ${zipFileName}`);
      saveAs(zipBlob, zipFileName);
      
      // Tamamlandı
      setProgressData(prev => ({ 
        ...prev, 
        status: 'completed', 
        currentTable: t('qrMenuTablesPage.bulkDownload.qrCodesDownloaded', { count: qrResults.length })
      }));
      
    } catch (error) {
      console.error('Toplu QR kod oluşturma hatası:', error);
      setProgressData(prev => ({ 
        ...prev, 
        status: 'error', 
        errorMessage: error instanceof Error ? error.message : t('qrMenuTablesPage.bulkDownload.unknownErrorOccurred')
      }));
    } finally {
      setDownloadingBulkQR(null);
    }
  };

  const handleGoBack = () => {
    isManager && onNavigateBack ? onNavigateBack() : navigate('/qr-menu-management');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const visibleMenus = initialMenuId
    ? qrMenus.filter((menu) => menu.id === initialMenuId)
    : qrMenus;

  return (
    <ScrollableContent>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? 1 : 2, 
        mb: isMobile ? 2 : 3 
      }}>
        {isMobile ? (
          <IconButton
            onClick={handleGoBack}
            sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            variant="outlined"
          >
            {t('qrMenuTablesPage.actions.back')}
          </Button>
        )}
        <TableRestaurantIcon sx={{ fontSize: isMobile ? 24 : 32, color: 'warning.main' }} />
        <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
          {isMobile ? t('qrMenuTablesPage.mobileTitle') : t('qrMenuTablesPage.title')}
        </Typography>
        <GuideButton guideKey="sales.qrTables" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : visibleMenus.length === 0 ? (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <RestaurantMenuIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('qrMenuTablesPage.emptyState.noQrMenuTitle')}
          </Typography>
          <Typography color="text.secondary">
            {t('qrMenuTablesPage.emptyState.noQrMenuDescription')}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {visibleMenus.map((menu) => (
            <Accordion
              key={menu.id}
              expanded={expandedMenus.has(menu.id)}
              onChange={handleAccordionChange(menu.id)}
              sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'primary.light',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': {
                    borderRadius: '8px 8px 0 0',
                  },
                  px: isMobile ? 1.5 : 2,
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: isMobile ? 1 : 2, 
                  width: '100%',
                  flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                  <RestaurantMenuIcon color="primary" sx={{ fontSize: isMobile ? 20 : 24 }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {/* Mobil: Menü adı ve şube tek satırda */}
                    {isMobile ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body1" fontWeight="bold" noWrap sx={{ maxWidth: '140px' }}>
                          {menu.name}
                        </Typography>
                        {menu.branchName && (
                          <Chip 
                            label={menu.branchName}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 0.75 }
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      /* Masaüstü: Menü adı ve şube alt alta */
                      <>
                        <Typography variant="h6" fontWeight="bold">
                          {menu.name}
                        </Typography>
                        {menu.branchName && (
                          <Typography variant="body2" color="text.secondary">
                            {t('qrMenuTablesPage.branchLabel', { branchName: menu.branchName })}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {expandedMenus.has(menu.id) && (
                      <>
                        <Chip 
                          label={
                            isMobile
                              ? `${tablesData[menu.id]?.length || 0}`
                              : t('qrMenuTablesPage.tableCount', { count: tablesData[menu.id]?.length || 0 })
                          }
                          color="warning"
                          variant="filled"
                          size="small"
                          icon={isMobile ? <TableRestaurantIcon sx={{ fontSize: 14 }} /> : undefined}
                          sx={{ 
                            color: 'white',
                            fontWeight: 'bold',
                            height: isMobile ? 24 : 32,
                            '& .MuiChip-label': { px: isMobile ? 0.75 : 1 }
                          }}
                        />
                        {tablesData[menu.id] && tablesData[menu.id].length > 0 && (
                          <Tooltip title={t('qrMenuTablesPage.tooltips.downloadAllQrCodes')}>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={downloadingBulkQR === menu.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadBulkQR(menu.id, menu.name, tablesData[menu.id]);
                              }}
                              sx={{ 
                                bgcolor: 'success.main',
                                color: 'white',
                                width: isMobile ? 28 : 32,
                                height: isMobile ? 28 : 32,
                                '&:hover': { bgcolor: 'success.dark' },
                                '&:disabled': { bgcolor: 'grey.400' }
                              }}
                            >
                              {downloadingBulkQR === menu.id ? (
                                <CircularProgress size={isMobile ? 14 : 16} color="inherit" />
                              ) : (
                                <DownloadIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 3 }}>
                {tablesData[menu.id] ? (
                  tablesData[menu.id].length > 0 ? (
                    <Grid container spacing={2}>
                      {tablesData[menu.id].map((table) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} xl={1.5} key={table.id}>
                          <TableCard>
                            <CardContent sx={{ textAlign: 'center', p: 1, '&:last-child': { pb: 1 } }}>
                              <TableIcon />
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                {table.tableName || t('qrMenuTablesPage.table.tableWithNumber', { number: table.tableNumber })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                                {formatDate(table.createdAt)}
                              </Typography>
                              
                              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                <Tooltip title={t('qrMenuTablesPage.tooltips.editTableName')}>
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleEditTable(menu.id, table)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <EditIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('qrMenuTablesPage.tooltips.downloadQrCode')}>
                                  <IconButton 
                                    size="small" 
                                    color="info" 
                                    sx={{ p: 0.5 }}
                                    onClick={() => handleDownloadTableQR(menu.id, table)}
                                  >
                                    <QrCodeIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('qrMenuTablesPage.tooltips.deleteTable')}>
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteTableClick(menu.id, table)}
                                    disabled={deletingTable === table.id}
                                    sx={{ p: 0.5 }}
                                  >
                                    {deletingTable === table.id ? (
                                      <CircularProgress size={12} color="inherit" />
                                    ) : (
                                      <DeleteIcon fontSize="inherit" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </CardContent>
                          </TableCard>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <TableRestaurantIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('qrMenuTablesPage.emptyState.noTableInMenuTitle')}
                      </Typography>
                      <Typography color="text.secondary">
                        {t('qrMenuTablesPage.emptyState.noTableInMenuDescription')}
                      </Typography>
                    </Box>
                  )
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Masa Adı Düzenleme Modal */}
      <Dialog open={!!editingTable} onClose={() => setEditingTable(null)}>
        <DialogTitle>{t('qrMenuTablesPage.editDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('qrMenuTablesPage.editDialog.tableName')}
            fullWidth
            variant="outlined"
            value={newTableName}
            onChange={(e) => setNewTableName(e.target.value)}
            helperText={t('qrMenuTablesPage.editDialog.tableNameHelp')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTable(null)}>{t('qrMenuTablesPage.actions.cancel')}</Button>
          <Button onClick={handleSaveTableName} variant="contained">
            {t('qrMenuTablesPage.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Masa Silme Onay Modal */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => {
          if (!deletingTable) {
            setDeleteConfirmOpen(false);
            setTableToDelete(null);
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {t('qrMenuTablesPage.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {t('qrMenuTablesPage.deleteDialog.warningTitle')}
            </Typography>
            <Typography variant="body2">
              {t('qrMenuTablesPage.deleteDialog.warningDescription')}
            </Typography>
          </Alert>
          <Typography variant="body1">
            {t('qrMenuTablesPage.deleteDialog.confirmQuestion', {
              tableName: tableToDelete?.table.tableName
                || t('qrMenuTablesPage.table.tableWithNumber', { number: tableToDelete?.table.tableNumber || '' })
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('qrMenuTablesPage.deleteDialog.relatedQrWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setTableToDelete(null);
            }}
            disabled={!!deletingTable}
          >
            {t('qrMenuTablesPage.actions.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteTable} 
            variant="contained"
            color="error"
            disabled={!!deletingTable}
            startIcon={deletingTable ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deletingTable ? t('qrMenuTablesPage.actions.deleting') : t('qrMenuTablesPage.actions.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toplu QR İndirme Progress Modal */}
      <Dialog 
        open={progressModalOpen} 
        onClose={progressData.status === 'completed' ? () => setProgressModalOpen(false) : undefined}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={progressData.status !== 'completed'}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DownloadIcon color="primary" />
            <Box>
              <Typography variant="h6">{t('qrMenuTablesPage.bulkDownload.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {progressData.menuName} - {t('qrMenuTablesPage.tableCount', { count: progressData.totalTables })}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Genel Progress */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {progressData.status === 'preparing' && t('qrMenuTablesPage.progress.preparing')}
                {progressData.status === 'generating' && t('qrMenuTablesPage.progress.generating')}
                {progressData.status === 'zipping' && t('qrMenuTablesPage.progress.zipping')}
                {progressData.status === 'completed' && t('qrMenuTablesPage.progress.completed')}
                {progressData.status === 'error' && t('qrMenuTablesPage.progress.error')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progressData.completedTables} / {progressData.totalTables}
              </Typography>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={(progressData.completedTables / progressData.totalTables) * 100}
              sx={{ height: 8, borderRadius: 4 }}
              color={progressData.status === 'error' ? 'error' : progressData.status === 'completed' ? 'success' : 'primary'}
            />
            
            {progressData.currentTable && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {progressData.currentTable}
              </Typography>
            )}
          </Box>

          {/* Hata Mesajı */}
          {progressData.status === 'error' && progressData.errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {progressData.errorMessage}
            </Alert>
          )}

          {/* Başarı Mesajı */}
          {progressData.status === 'completed' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('qrMenuTablesPage.bulkDownload.allDone')}
            </Alert>
          )}

          {/* Detaylı Progress Listesi */}
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('qrMenuTablesPage.bulkDownload.tableDetails')}
            </Typography>
            <List dense>
              {progressData.tableProgress.map((tableProgress, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {tableProgress.status === 'waiting' && <HourglassEmptyIcon color="disabled" />}
                    {tableProgress.status === 'processing' && <CircularProgress size={20} />}
                    {tableProgress.status === 'completed' && <CheckCircleIcon color="success" />}
                    {tableProgress.status === 'error' && <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {t('qrMenuTablesPage.table.tableWithNumber', { number: tableProgress.tableNumber })}
                        {tableProgress.tableName && ` (${tableProgress.tableName})`}
                      </Typography>
                    }
                    secondary={
                      tableProgress.status === 'error' && tableProgress.error ? (
                        <Typography variant="caption" color="error">
                          {tableProgress.error}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {tableProgress.status === 'waiting' && t('qrMenuTablesPage.progress.waiting')}
                          {tableProgress.status === 'processing' && t('qrMenuTablesPage.progress.processing')}
                          {tableProgress.status === 'completed' && t('qrMenuTablesPage.progress.completedShort')}
                          {tableProgress.status === 'error' && t('qrMenuTablesPage.progress.errorShort')}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        
        <DialogActions>
          {progressData.status === 'completed' || progressData.status === 'error' ? (
            <Button onClick={() => setProgressModalOpen(false)} variant="contained">
              {t('qrMenuTablesPage.actions.close')}
            </Button>
          ) : (
            <Button disabled variant="outlined">
              {t('qrMenuTablesPage.actions.processing')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ScrollableContent>
  );
};

export default TablesPage;
